/**
 * Mobile League Message Board — CD delivery `League Message Board.dc.html` option 5
 * "Feed + reactions" (CLAUDE.md §121-122 "LOCKED — League Message Board"): LeagueChrome
 * (active messages, parent) → stats strip (N messages · M unread + Refresh) → feed of
 * FLAT message panels (screenTop bg, 1px border, NO curve; pinned = gold left-rule + 📌)
 * → composer → BottomNav. Each panel: avatar + owner + team + (edited) + time + 📌, body,
 * optional images, nested replies (indented + 2px gold left-border), footer = Reply(count)
 * + Edit/Pin/Delete (own/commish gated). Canonical fields (owner_name, team_name,
 * team_logo_url, message_text, is_pinned, is_edited, reply_count, replies).
 *
 * REUSE: MobileTeamLogo (square team-logo avatar via canonical team_logo_url, same as the
 * League Home preview) + the message data layer (getMessages / getMessageStats /
 * postMessage / updateMessage / deleteMessage / togglePinMessage — Rule 11, real shapes)
 * + the gating (commissioner = league.role; own = user.user_id via useAuth, since the parent
 * passes user=null) + the canonical S3 image upload (leaguesAPI.uploadMessageImage →
 * presigned PUT; MIME allowlist, 5MB, 4-image cap). NEW vs the preview: the full feed, the
 * flat panel + nested replies, the composer (post + image attach), inline reply (+ image
 * attach) / edit, pin/delete. Reactions are display-only in the mock with NO backend
 * endpoint → omitted (not invented).
 *
 * Tokens only, zero hex; flush, no rounded cards; hidden scrollbars; the mock has no
 * section labels (stats strip → feed → composer), so no MobileSectionHeader here.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { dynastyTheme } from '../../../services/colorService';
import { leaguesAPI } from '../../../services/apiService';
import { useAuth } from '../../../contexts/AuthContext';
import MobileTeamLogo from '../components/MobileTeamLogo';

const C = dynastyTheme.tokens.colors;

// Image upload — canonical S3 path (leaguesAPI.uploadMessageImage → presigned PUT).
// MIME allowlist + 5MB + 4-image cap.
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
const MAX_IMAGES_PER_MESSAGE = 4;
const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024; // 5MB

const timeAgo = (iso) => {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (isNaN(then)) return '';
  const s = Math.max(0, (Date.now() - then) / 1000);
  if (s < 60) return 'now';
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  if (s < 604800) return `${Math.floor(s / 86400)}d`;
  if (s < 2592000) return `${Math.floor(s / 604800)}w`;
  return `${Math.floor(s / 2592000)}mo`;
};

// Inline icons (stroke = currentColor) — mirror the mock's SVGs.
const Ico = ({ d, w = 11 }) => (
  <svg viewBox="0 0 24 24" width={w} height={w} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{d}</svg>
);
const RefreshIco = () => <Ico d={<path d="M20 11A8 8 0 006 5.3L3 8M4 13a8 8 0 0014 5.7L21 16" />} />;
const ReplyIco = () => <Ico d={<path d="M9 17l-5-5 5-5M4 12h11a5 5 0 015 5v1" />} />;
const EditIco = () => <Ico d={<path d="M12 20h9M16.5 3.5a2 2 0 013 3L7 19l-4 1 1-4z" />} />;
const PinIco = () => <Ico d={<path d="M12 17v5M9 3h6l-1 7 3 2H7l3-2z" />} />;
const DeleteIco = () => <Ico d={<path d="M4 7h16M9 7V5h6v2M6 7l1 13h10l1-13" />} />;
const SendIco = () => <Ico w={13} d={<path d="M22 2L11 13M22 2l-7 20-4-9-9-4z" />} />;
const ImageIco = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M21 15l-5-5L5 21" /></svg>
);

const ACT = { display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 10, fontWeight: 700, color: C.muted, background: 'none', border: 'none', cursor: 'pointer', padding: 0 };

// Image button (opens the native file picker) + picked-thumbnail strip with remove.
const ImageAttach = ({ count, onAdd }) => {
  const ref = React.useRef(null);
  const atLimit = count >= MAX_IMAGES_PER_MESSAGE;
  return (
    <>
      <input
        ref={ref} type="file" accept="image/*" multiple style={{ display: 'none' }}
        onChange={(e) => { if (e.target.files && e.target.files.length) onAdd(e.target.files); e.target.value = ''; }}
      />
      <button
        onClick={() => ref.current && ref.current.click()} disabled={atLimit} aria-label="Add image"
        style={{ width: 30, height: 30, borderRadius: 5, flexShrink: 0, border: `1px solid ${C.border}`, background: C.ground.screenTop, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.muted, cursor: atLimit ? 'default' : 'pointer', opacity: atLimit ? 0.45 : 1 }}
      ><ImageIco /></button>
    </>
  );
};
const Thumbs = ({ images, onRemove }) => (images && images.length ? (
  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 6 }}>
    {images.map((img, i) => (
      <div key={i} style={{ position: 'relative' }}>
        <img src={img.previewUrl || img.url} alt="" style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 3, border: `1px solid ${C.border}` }} />
        <span onClick={() => onRemove(i)} style={{ position: 'absolute', top: -5, right: -5, width: 15, height: 15, borderRadius: '50%', background: C.error, color: C.white, fontSize: 9, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>✕</span>
      </div>
    ))}
  </div>
) : null);

function MobileLeagueMessages({ league, leagueId }) {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState({ total: 0, unread: 0 });
  const [loading, setLoading] = useState(true);
  const [composerText, setComposerText] = useState('');
  const [sending, setSending] = useState(false);
  const [replyingToId, setReplyingToId] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const [composerImages, setComposerImages] = useState([]);
  const [replyImages, setReplyImages] = useState([]);
  const [uploadingImages, setUploadingImages] = useState(false);
  const [error, setError] = useState(null);

  const isCommissioner = league?.role === 'commissioner';
  const isOwn = useCallback((m) => Boolean(user?.user_id) && m.user_id === user.user_id, [user]);

  // ── Image upload (canonical S3 path; validate → wrap → presigned PUT) ───────
  const validateAndWrapFiles = useCallback((files) => {
    const wrapped = [];
    const errors = [];
    Array.from(files).forEach((file) => {
      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) { errors.push(`Invalid type: ${file.name}. Only JPG, PNG, GIF, WEBP.`); return; }
      if (file.size > MAX_FILE_SIZE_BYTES) { errors.push(`Too large: ${file.name}. Max 5MB.`); return; }
      wrapped.push({ file, previewUrl: URL.createObjectURL(file) });
    });
    return { wrapped, errors };
  }, []);

  const revokePreviews = useCallback((list) => {
    (list || []).forEach((entry) => { if (entry && entry.previewUrl) URL.revokeObjectURL(entry.previewUrl); });
  }, []);

  const handleAddImages = useCallback((files, target) => {
    const { wrapped, errors } = validateAndWrapFiles(files);
    if (errors.length > 0) setError(errors.join(' '));
    const setter = target === 'reply' ? setReplyImages : setComposerImages;
    setter((prev) => {
      const merged = [...prev, ...wrapped];
      if (merged.length > MAX_IMAGES_PER_MESSAGE) {
        const discarded = wrapped.slice(MAX_IMAGES_PER_MESSAGE - prev.length);
        revokePreviews(discarded);
        setError(`Maximum ${MAX_IMAGES_PER_MESSAGE} images per message.`);
        return merged.slice(0, MAX_IMAGES_PER_MESSAGE);
      }
      return merged;
    });
  }, [validateAndWrapFiles, revokePreviews]);

  const handleRemoveImage = useCallback((index, target) => {
    const list = target === 'reply' ? replyImages : composerImages;
    const removed = list[index];
    if (removed && removed.previewUrl) URL.revokeObjectURL(removed.previewUrl);
    const setter = target === 'reply' ? setReplyImages : setComposerImages;
    setter((prev) => prev.filter((_, i) => i !== index));
  }, [composerImages, replyImages]);

  // Upload all File-bearing entries; keep already-uploaded URL entries. Throws on failure.
  const uploadAllImages = useCallback(async (list) => {
    if (!list || list.length === 0) return [];
    setUploadingImages(true);
    try {
      const urls = await Promise.all(list.map(async (entry) => {
        if (entry.url) return entry.url;
        const result = await leaguesAPI.uploadMessageImage(leagueId, entry.file);
        if (!result || !result.success || !result.image_url) throw new Error('Upload failed');
        return result.image_url;
      }));
      return urls;
    } finally {
      setUploadingImages(false);
    }
  }, [leagueId]);

  const load = useCallback(async () => {
    try {
      const [msgRes, statRes] = await Promise.all([
        leaguesAPI.getMessages(leagueId, 50, 0, true),
        leaguesAPI.getMessageStats(leagueId),
      ]);
      setMessages(msgRes?.messages || msgRes?.data || []);
      setStats({
        total: statRes?.total_messages ?? statRes?.total ?? 0,
        unread: statRes?.unread_count ?? statRes?.unread ?? 0,
      });
    } catch (err) {
      console.error('Error loading messages:', err);
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  useEffect(() => { if (leagueId) load(); }, [leagueId, load]);

  const post = async () => {
    const text = composerText.trim();
    if ((!text && composerImages.length === 0) || sending) return;
    setSending(true);
    setError(null);
    try {
      const uploadedUrls = await uploadAllImages(composerImages);
      const res = await leaguesAPI.postMessage(leagueId, { message_text: text, image_urls: uploadedUrls.length > 0 ? uploadedUrls : null });
      if (res?.success) { setComposerText(''); revokePreviews(composerImages); setComposerImages([]); await load(); }
    } catch (err) { console.error('Post failed:', err); setError(err.response?.data?.detail || err.message || 'Failed to post message.'); } finally { setSending(false); }
  };

  const sendReply = async (parentId) => {
    const text = replyText.trim();
    if ((!text && replyImages.length === 0) || sending) return;
    setSending(true);
    setError(null);
    try {
      const uploadedUrls = await uploadAllImages(replyImages);
      const res = await leaguesAPI.postMessage(leagueId, { message_text: text, image_urls: uploadedUrls.length > 0 ? uploadedUrls : null, parent_message_id: parentId });
      if (res?.success) { setReplyingToId(null); setReplyText(''); revokePreviews(replyImages); setReplyImages([]); await load(); }
    } catch (err) { console.error('Reply failed:', err); setError(err.response?.data?.detail || err.message || 'Failed to post reply.'); } finally { setSending(false); }
  };

  const saveEdit = async (id) => {
    const text = editText.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      const res = await leaguesAPI.updateMessage(leagueId, id, { message_text: text });
      if (res?.success) { setEditingId(null); setEditText(''); await load(); }
    } catch (err) { console.error('Edit failed:', err); } finally { setSending(false); }
  };

  const pin = async (m) => {
    try { await leaguesAPI.togglePinMessage(leagueId, m.message_id, !m.is_pinned); await load(); }
    catch (err) { console.error('Pin failed:', err); }
  };
  const remove = async (id) => {
    // eslint-disable-next-line no-alert
    if (typeof window !== 'undefined' && !window.confirm('Delete this message?')) return;
    try { await leaguesAPI.deleteMessage(leagueId, id); await load(); }
    catch (err) { console.error('Delete failed:', err); }
  };

  // Compact input style (composer + reply + edit).
  const inputStyle = {
    flex: 1, minWidth: 0, background: C.ground.screenTop, border: `1px solid ${C.border}`,
    borderRadius: 5, padding: '8px 10px', fontSize: 11, color: C.textHi, outline: 'none', fontFamily: 'inherit', resize: 'none',
  };

  const ReplyRow = ({ r }) => (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
        <MobileTeamLogo name={r.owner_name || r.team_name} logoUrl={r.team_logo_url} size={18} />
        <span style={{ fontSize: 11, fontWeight: 700, color: C.textHi }}>{r.owner_name || 'Unknown'}</span>
        <span style={{ fontSize: 8, color: C.muted, marginLeft: 'auto' }}>{timeAgo(r.created_at)}</span>
      </div>
      <div style={{ fontSize: 11.5, lineHeight: 1.4, color: C.coolGray[150] }}>{r.message_text}</div>
    </div>
  );

  return (
    <div style={{ paddingBottom: 4 }}>
      <style>{`.msg-noscroll::-webkit-scrollbar{display:none;}`}</style>

      {/* Stats strip + Refresh */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '7px 14px', borderBottom: `1px solid ${C.border}`, fontSize: 11, color: C.muted }}>
        <span><b style={{ color: C.textHi }}>{stats.total}</b> messages · <b style={{ color: C.primary }}>{stats.unread} unread</b></span>
        <button onClick={load} style={{ ...ACT, color: C.primary }}><RefreshIco />Refresh</button>
      </div>

      {/* Composer (text field + image attach + gold Send) */}
      <div style={{ padding: '8px 12px', borderBottom: `1px solid ${C.border}`, background: C.ground.screen }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            value={composerText} onChange={(e) => setComposerText(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') post(); }}
            placeholder="Write a message…" style={inputStyle}
          />
          <ImageAttach count={composerImages.length} onAdd={(files) => handleAddImages(files, 'composer')} />
          <button onClick={post} disabled={(!composerText.trim() && composerImages.length === 0) || sending || uploadingImages} style={{
            display: 'flex', alignItems: 'center', gap: 5, flexShrink: 0, background: C.primary, color: C.neutral[950],
            border: 'none', borderRadius: 5, padding: '8px 13px', fontSize: 12, fontWeight: 800, cursor: 'pointer',
            opacity: ((!composerText.trim() && composerImages.length === 0) || sending || uploadingImages) ? 0.45 : 1,
          }}><SendIco />{uploadingImages ? 'Uploading…' : 'Send'}</button>
        </div>
        <Thumbs images={composerImages} onRemove={(i) => handleRemoveImage(i, 'composer')} />
        {error && <div style={{ fontSize: 10, color: C.error, marginTop: 5 }}>{error}</div>}
      </div>

      {/* Feed */}
      {loading ? (
        <div style={{ padding: 30, textAlign: 'center', color: C.muted, fontSize: 12 }}>Loading messages…</div>
      ) : messages.length === 0 ? (
        <div style={{ padding: 30, textAlign: 'center', color: C.muted, fontSize: 12 }}>No messages yet — start the conversation.</div>
      ) : (
        <div className="msg-noscroll" style={{ display: 'flex', flexDirection: 'column', gap: 9, padding: '10px 12px' }}>
          {messages.map((m) => {
            const canEdit = isOwn(m);
            const canDelete = isOwn(m) || isCommissioner;
            const canPin = isCommissioner && !m.parent_message_id;
            const editing = editingId === m.message_id;
            return (
              <div key={m.message_id} style={{
                background: C.ground.screenTop, border: `1px solid ${C.border}`,
                ...(m.is_pinned ? { borderLeft: `3px solid ${C.primary}` } : null),
              }}>
                <div style={{ padding: '9px 11px' }}>
                  {/* header */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 4 }}>
                    <MobileTeamLogo name={m.owner_name || m.team_name} logoUrl={m.team_logo_url} size={22} />
                    <span style={{ fontSize: 12, fontWeight: 800, color: C.textHi }}>{m.owner_name || 'Unknown'}</span>
                    {m.team_name && <span style={{ fontSize: 9, color: C.muted }}>{m.team_name}</span>}
                    {m.is_edited && <span style={{ fontSize: 8, color: C.muted }}>(edited)</span>}
                    <span style={{ fontSize: 8, color: C.muted, marginLeft: 'auto' }}>{timeAgo(m.created_at)}</span>
                    {m.is_pinned && <span style={{ color: C.primary, fontSize: 10 }}>📌</span>}
                  </div>
                  {/* body / edit */}
                  {editing ? (
                    <div>
                      <textarea value={editText} onChange={(e) => setEditText(e.target.value)} rows={2} style={{ ...inputStyle, width: '100%', boxSizing: 'border-box' }} />
                      <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                        <button onClick={() => saveEdit(m.message_id)} style={{ ...ACT, color: C.primary }}>Save</button>
                        <button onClick={() => { setEditingId(null); setEditText(''); }} style={ACT}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ fontSize: 12.5, color: C.coolGray[150], lineHeight: 1.45 }}>{m.message_text}</div>
                  )}
                  {/* images (read-only; upload not wired this pass) */}
                  {Array.isArray(m.image_urls) && m.image_urls.length > 0 && (
                    <div style={{ display: 'flex', gap: 6, marginTop: 7, flexWrap: 'wrap' }}>
                      {m.image_urls.map((u, i) => (
                        <img key={i} src={u} alt="" style={{ width: 96, height: 66, objectFit: 'cover', borderRadius: 3, border: `1px solid ${C.border}` }} />
                      ))}
                    </div>
                  )}
                  {/* nested replies (indented + 2px gold left-border) */}
                  {Array.isArray(m.replies) && m.replies.length > 0 && (
                    <div style={{ marginTop: 9, paddingLeft: 10, borderLeft: `2px solid ${C.primaryTint.t15}`, display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {m.replies.map((r) => <ReplyRow key={r.message_id} r={r} />)}
                    </div>
                  )}
                  {/* inline reply composer (text + image attach) */}
                  {replyingToId === m.message_id && (
                    <div style={{ marginTop: 8 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
                        <input value={replyText} onChange={(e) => setReplyText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') sendReply(m.message_id); }} placeholder="Write a reply…" style={inputStyle} autoFocus />
                        <ImageAttach count={replyImages.length} onAdd={(files) => handleAddImages(files, 'reply')} />
                        <button onClick={() => sendReply(m.message_id)} disabled={(!replyText.trim() && replyImages.length === 0) || sending || uploadingImages} style={{ ...ACT, color: C.primary, opacity: ((!replyText.trim() && replyImages.length === 0) || sending || uploadingImages) ? 0.45 : 1 }}>{uploadingImages ? 'Uploading…' : 'Send'}</button>
                      </div>
                      <Thumbs images={replyImages} onRemove={(i) => handleRemoveImage(i, 'reply')} />
                    </div>
                  )}
                </div>
                {/* footer actions (no reactions — no backend endpoint) */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '6px 11px', borderTop: `1px solid ${C.hairline}` }}>
                  {!m.parent_message_id && (
                    <button onClick={() => { revokePreviews(replyImages); setReplyImages([]); setReplyingToId(replyingToId === m.message_id ? null : m.message_id); setReplyText(''); }} style={{ ...ACT, color: C.primary, marginLeft: 'auto' }}>
                      <ReplyIco />Reply{m.reply_count > 0 ? ` (${m.reply_count})` : ''}
                    </button>
                  )}
                  {canEdit && <button onClick={() => { setEditingId(m.message_id); setEditText(m.message_text || ''); }} style={{ ...ACT, marginLeft: m.parent_message_id ? 'auto' : 0 }}><EditIco /></button>}
                  {canPin && <button onClick={() => pin(m)} style={{ ...ACT, color: m.is_pinned ? C.primary : C.muted }}><PinIco /></button>}
                  {canDelete && <button onClick={() => remove(m.message_id)} style={{ ...ACT, color: C.error }}><DeleteIco /></button>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default MobileLeagueMessages;
